import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { setCredentials } from '../../slices/authApiSlice';
import { 
  useAdminReadUserByIdQuery, 
  useAdminUpdateUserByIdMutation, 
  useAdminDeleteUserByIdMutation,
} from '../../slices/usersApiSlice';
import FormContainer from '../../components/FormContainer';
import Loader from '../../components/Loader';
import Message from '../../components/Message';


export default function ReadUpdateUserScreen() {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // userId = the user being edited (from the URL); adminId = the signed-in admin
  const { userId } = useParams();
  const adminId = userInfo?._id;

  const { 
    data: user, 
    isLoading,
    error
  } = useAdminReadUserByIdQuery({
    adminId,
    userId
  });

  const [updateUser, { isLoading: isUpdating }] = 
    useAdminUpdateUserByIdMutation();

  const [deleteUser, { isLoading: isDeleting }] =
    useAdminDeleteUserByIdMutation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    bizName: '',
    primaryEmail: '',
    secondaryEmail: '',
    primaryPhone: '',
    secondaryPhone: '',
    isSubscribedToEmail: false,
    isSubscribedToText: false,
    isAdmin: false,
    adminNotes: ''
  });

  const { 
    firstName,
    lastName,
    bizName,
    primaryEmail,
    secondaryEmail,
    primaryPhone,
    secondaryPhone,
    isSubscribedToEmail,
    isSubscribedToText,
    isAdmin,
    adminNotes
  } = formData;


  useEffect(() => { 
    if (user) { 
      const { 
        firstName = '',
        lastName = '',
        bizName = '',
        primaryEmail = '',
        secondaryEmail = '',
        primaryPhone = '',
        secondaryPhone = '',
        isSubscribedToEmail = false,
        isSubscribedToText = false,
        isAdmin = false,
        adminNotes = ''
      } = user;
    
      setFormData((prev) => ({ 
        ...prev,
        firstName,
        lastName,
        bizName,
        primaryEmail,
        secondaryEmail,
        primaryPhone,
        secondaryPhone,
        isSubscribedToEmail,
        isSubscribedToText,
        isAdmin,
        adminNotes
      })); 
    } 
  }, [user]);

  const hasValidUser = Boolean(
    user && (
      user._id || user.firstName || user.lastName || user.primaryEmail || user.password || user.isAdmin
    )
  );
  
  if (!user) return <Loader />;

  async function submitHandler(e) { 
    e.preventDefault();
    try {
      const updatedUser = await updateUser({
        adminId,
        userId, 
        ...formData
      })
      .unwrap();
      // If the admin updated their own account, update auth state
      if (updatedUser._id === userInfo._id) {
        dispatch(setCredentials(updatedUser));
      }
      toast.success(`Success: User ${userId} ${firstName} ${lastName} has been updated`);
      navigate(`/admin/all_users`);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const renderError = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.data?.message) return error.data.message;
    if (error.error) return error.error;
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  };

  const deleteUserHandler = async () => {
    if (window.confirm(`Are you sure you want to delete user ${firstName} ${lastName}? This CANNOT be undone.`)) {
      try {
        await deleteUser({ adminId, userId }).unwrap();
        toast.success(`Success: User ${firstName} ${lastName} is deleted`);
        navigate(`/admin/all_users`);
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  return (
    <>
      <Link to={`/admin/all_users`} className='btn btn-light' style={{ marginTop: '32px', marginBottom: '16px' }}>
        Return to All Users
      </Link>
      <FormContainer>
        <h1 style={{ marginTop: '32px' }}>Edit User</h1>
        <h5>* = Required field</h5>
        
        { isLoading ? ( 
          <Loader /> 
        ) : error ? ( 
          <Message variant='danger'>{renderError(error)}</Message>
        ) : !hasValidUser ? (
          <Message variant='warning'>Failure: User not found</Message>
        ) : ( 
            <Form onSubmit={ submitHandler }>
              <Form.Group controlId='firstName' className='my-2 mb-3'>
                <Form.Label>*First Name</Form.Label>
                <Form.Control 
                  type='text'
                  placeholder='First name'
                  value={firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <Form.Group controlId='lastName' className='my-2 mb-3'>
                <Form.Label>*Last Name</Form.Label>
                <Form.Control 
                  type='text'
                  placeholder='Last name'
                  value={lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <Form.Group controlId='bizName' className='my-2 mb-3'>
                <Form.Label>Business Name</Form.Label>
                <Form.Control 
                  type='text'
                  placeholder='Business name, if applicable'
                  value={bizName}
                  onChange={(e) => setFormData({ ...formData, bizName: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <Form.Group controlId='primaryEmail' className='mt-2 mb-3'>
                <Form.Label>*Primary E-Mail (part of the user's 'sign in' credentials)</Form.Label>
                <Form.Control 
                  type='email'
                  placeholder='Primary e-mail address'
                  value={primaryEmail}
                  onChange={(e) => setFormData({ ...formData, primaryEmail: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <Form.Group controlId='secondaryEmail' className='mt-2 mb-3'>
                <Form.Label>Secondary E-Mail</Form.Label>
                <Form.Control 
                  type='email'
                  placeholder='Secondary e-mail address'
                  value={secondaryEmail}
                  onChange={(e) => setFormData({ ...formData, secondaryEmail: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <Form.Group controlId='primaryPhone' className='mt-2 mb-3'>
                <Form.Label>Primary Phone</Form.Label>
                <Form.Control 
                  type='phone'
                  placeholder='Primary phone number'
                  value={primaryPhone}
                  onChange={(e) => setFormData({ ...formData, primaryPhone: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <Form.Group controlId='secondaryPhone' className='mt-2 mb-3'>
                <Form.Label>Secondary Phone</Form.Label>
                <Form.Control 
                  type='phone'
                  placeholder='Secondary phone number'
                  value={secondaryPhone}
                  onChange={(e) => setFormData({ ...formData, secondaryPhone: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <Form.Group controlId='isSubscribedToEmail' className='my-2 mb-3'>
                <Form.Check 
                  type='checkbox'
                  label="Is Subscribed To Promotional E-Mail Messaging"
                  checked={isSubscribedToEmail}
                  onChange={(e) => setFormData({ ...formData, isSubscribedToEmail: e.target.checked }) }
                ></Form.Check>
              </Form.Group>
              <Form.Group controlId='isSubscribedToText' className='my-2 mb-3'>
                <Form.Check 
                  type='checkbox'
                  label="Is Subscribed To Promotional Text Messaging"
                  checked={isSubscribedToText}
                  onChange={(e) => setFormData({ ...formData, isSubscribedToText: e.target.checked }) }
                ></Form.Check>
              </Form.Group>
              <Form.Group controlId='isAdmin' className='my-2 mb-3'>
                <Form.Check 
                  type='checkbox'
                  label="Is Administrator"
                  checked={isAdmin}
                  onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked }) }
                  disabled={(user._id === userInfo._id)}
                ></Form.Check>
              </Form.Group>
              <Form.Group controlId='adminNotes' className='my-2 mb-3'>
                <Form.Label>Administrator Notes</Form.Label>
                <Form.Control 
                  as='textarea'
                  rows={12}
                  placeholder='Notes, if applicable'
                  value={adminNotes}
                  onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value }) }
                ></Form.Control>
              </Form.Group>
              <div className='d-flex justify-content-between' style={{ marginBottom: '32px' }}>
                <Link to={`/admin/all_users`} className='btn btn-light my-2 text-decoration-none'>
                  Cancel
                </Link>
                <Button 
                  type='submit'
                  variant='primary'
                  className='my-2'
                >
                  Save
                </Button>
              </div>
              {isUpdating && <Loader />}
            </Form>
          )
        }
      </FormContainer>

      { hasValidUser && user._id !== userInfo._id && (
        <FormContainer>
          <Card className='border-danger mt-3 shadow-sm mb-5'>
            <Card.Header className='bg-danger text-white fw-bold'>
              Admin Danger Zone
            </Card.Header>
            <Card.Body className='text-center p-4'>
              <p className='mb-4'>
                Warning: Deleting this user is permanent and CANNOT be undone.
              </p>
              <Button
                variant='danger'
                size='lg'
                className='w-100 fw-bold py-3'
                onClick={deleteUserHandler}
              >
                DELETE
              </Button>
              {isDeleting && <Loader />}
            </Card.Body>
          </Card>
        </FormContainer>
      )}
    </>
  );
};